import * as React from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { MAX_PHOTOS, PHOTO_TYPES, photoProblem, preparePhoto } from '../lib/photos';


export default function PhotoPicker({ photos, onChange, disabled = false }) {
    const inputRef = React.useRef(null);
    const [error, setError] = React.useState('');
    const [busy, setBusy] = React.useState(false);

    
    const [previews, setPreviews] = React.useState([]);
    React.useEffect(() => {
        if (typeof URL.createObjectURL !== 'function') return undefined;
        const urls = photos.map((photo) => URL.createObjectURL(photo));
        setPreviews(urls);
        return () => urls.forEach((url) => URL.revokeObjectURL(url));
    }, [photos]);

    const handleFiles = async (event) => {
        const chosen = Array.from(event.target.files || []);
        event.target.value = ''; 
        if (chosen.length === 0) return;

        setBusy(true);
        const accepted = [...photos];
        let problem = '';
        for (const file of chosen) {
            if (accepted.length >= MAX_PHOTOS) {
                problem = `You can add up to ${MAX_PHOTOS} photos.`;
                break;
            }
            const prepared = await preparePhoto(file);
            const fileProblem = photoProblem(prepared);
            if (fileProblem) {
                problem = fileProblem;
            } else {
                accepted.push(prepared);
            }
        }
        setBusy(false);
        setError(problem);
        onChange(accepted);
    };

    const remove = (index) => {
        setError('');
        onChange(photos.filter((_, i) => i !== index));
    };

    return (
        <Box sx={{ mt: 2 }}>
            <input
                ref={inputRef}
                type="file"
                accept={PHOTO_TYPES.join(',')}
                multiple
                hidden
                data-testid="photo-input"
                onChange={handleFiles}
            />
            <Button
                variant="outlined"
                startIcon={<PhotoCameraIcon />}
                onClick={() => inputRef.current.click()}
                disabled={disabled || busy || photos.length >= MAX_PHOTOS}
            >
                {photos.length > 0 ? 'Add another photo' : 'Add photos'}
            </Button>
            <FormHelperText error={Boolean(error)}>
                {error || `Optional, up to ${MAX_PHOTOS} photos. ${photos.length}/${MAX_PHOTOS} added.`}
            </FormHelperText>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                {photos.map((photo, index) => (
                    <Box key={`${photo.name}-${index}`} sx={{ position: 'relative' }}>
                        {previews[index] ? (
                            <img
                                src={previews[index]}
                                alt={`Attachment ${index + 1}`}
                                style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 4, display: 'block' }}
                            />
                        ) : (
                            <Box sx={{ width: 72, height: 72, bgcolor: 'action.hover', borderRadius: 1 }} />
                        )}
                        <IconButton
                            size="small"
                            aria-label={`Remove photo ${index + 1}`}
                            onClick={() => remove(index)}
                            disabled={disabled}
                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', boxShadow: 1 }}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
